import type {
  NotificationPermission as CoreNotificationPermission,
  DeliveryCapability,
  Notifier,
  PendingReminder,
  ReminderSchedule,
  ScheduleResult,
  Timestamp,
} from '@anytracker/core'

/**
 * The web implementation of core's `Notifier` port, which does not deliver.
 *
 * ADR-0004 in one class. Two facts, neither of which effort can change:
 *
 *   - **A static site cannot send a web push.** The Web Push API needs a server
 *     holding VAPID keys to hand the message to the browser's push service. An
 *     S3 bucket cannot do that, and there is no client-only path.
 *   - **The page cannot schedule its own notification.** `TimestampTrigger` was
 *     a Chrome origin trial that never shipped, service workers are killed
 *     aggressively so a `setTimeout` inside one is unreliable past seconds, and
 *     Periodic Background Sync has roughly twelve-hour granularity.
 *
 * So `schedule()` returns an explicit `not-supported` with a reason the UI shows
 * verbatim. Permission reporting is real, because it costs nothing and the
 * answer is true.
 *
 * **Do not add a fallback that fires a `Notification` while the tab is open.**
 * ADR-0004 rejected that by name: it presents as a working feature while
 * failing exactly when a reminder is needed, which is when the app is closed.
 * It is the most likely thing for someone to add here in good faith, and it
 * would make the app quietly dishonest about the one feature it is already
 * being honest about.
 */
export class WebNotifier implements Notifier {
  /**
   * Shown to the user verbatim, so it says what is true and what happens next.
   *
   * Not "notifications are unavailable", which reads as a browser setting the
   * user could go and change.
   */
  static readonly REASON =
    'Reminders cannot be delivered by a website. Your schedules are saved and ' +
    'will start working when the mobile app arrives.'

  delivery(): Promise<DeliveryCapability> {
    return Promise.resolve({ status: 'unavailable', reason: WebNotifier.REASON })
  }

  permission(): Promise<CoreNotificationPermission> {
    if (typeof Notification === 'undefined') {
      return Promise.resolve('unsupported')
    }
    return Promise.resolve(Notification.permission as CoreNotificationPermission)
  }

  /**
   * Really prompts — and nothing on the web calls it.
   *
   * Kept honest rather than stubbed so the port has one true implementation of
   * this method, but T-702 deliberately never asks: a permission that would
   * grant nothing is a dialog that costs trust and buys none.
   */
  async requestPermission(): Promise<CoreNotificationPermission> {
    if (typeof Notification === 'undefined') {
      return 'unsupported'
    }
    return await Notification.requestPermission()
  }

  /*
   * Takes the arguments it ignores.
   *
   * A zero-argument implementation satisfies the interface but makes every call
   * site a type error, and shrinking the signature to match what this build
   * happens to do would be the port bending to its weakest implementation.
   */
  schedule(_schedule: ReminderSchedule, _at: Timestamp): Promise<ScheduleResult> {
    return Promise.resolve({ status: 'not-supported', reason: WebNotifier.REASON })
  }

  cancel(_scheduleId: string): Promise<void> {
    // Nothing was ever scheduled, so there is nothing to cancel and this is not
    // an error. A caller tidying up after deleting a reminder should not have
    // to know which platform it is on.
    return Promise.resolve()
  }

  pending(): Promise<readonly PendingReminder[]> {
    // Always empty, and truthfully so. Returning the *schedules* here would be
    // the same lie as firing a notification while the tab is open.
    return Promise.resolve([])
  }
}
